import { useOperationsStore } from '@operations/AppProvider'
import { ContainerPositionType, WorkInstructionStatus } from '@operations/app/api'
import { SelectOption } from '@operations/app/models'
import { observer } from 'mobx-react-lite'
import { DirectLoadOption, JobSteps } from '../../models/job-stepper.model'
import { CheckInformation } from './CheckInformation'
import { ConfirmWagonNumber } from './ConfirmWagonNumber'
import { ConfirmWeight } from './ConfirmWeight'
import { DirectLoadDestination } from './DirectLoadDestination'
import { EditDestination } from './EditDestination'
import { PlugReefer } from './PlugReefer'
import { RestowDestination } from './RestowDestination'
import { SelectContainer } from './SelectContainer'
import { SetContainerPosition } from './SetContainerPosition'
import { EquipmentOperatorProps } from './edit-destination.interface'

export const JobStepper = observer(
  ({
    yardSelector,
    ConfirmPickupComponent,
    RailcarAutocompleteComponent,
    handlePicks,
  }: EquipmentOperatorProps) => {
    const { equipmentOperatorStepperUIStore, equipmentOperatorUIStore, tenantConfigStore } =
      useOperationsStore()

    const handleChangeDestination = (value = true) => {
      equipmentOperatorStepperUIStore.setHasYardSlotChanged(value)
      equipmentOperatorStepperUIStore.setHasError()
    }

    const handleBlockingIssueChange = (hasBlockingIssue: boolean) => {
      equipmentOperatorStepperUIStore.setHasBlockingYardPositionIssue(hasBlockingIssue)
    }

    const handleChangeCargoUnit = (newValue?: SelectOption, origin?: string) => {
      equipmentOperatorStepperUIStore.setContainerNumberOptionAndOrigin(newValue, origin)
      equipmentOperatorStepperUIStore.setHasError()
    }

    const handleChangeRailcar = (newValue?: number) => {
      equipmentOperatorStepperUIStore.setRailcarId(newValue)
      equipmentOperatorStepperUIStore.setHasError()
    }

    const handleChangeWeight = (newValue?: number) => {
      equipmentOperatorStepperUIStore.setGrossWeight(newValue)
      equipmentOperatorStepperUIStore.setHasError()
    }

    const handleEquipmentAssignment = (id?: number) => {
      equipmentOperatorStepperUIStore.assignEquipment(id)
      equipmentOperatorStepperUIStore.setHasError()
    }

    const handlePluggedIn = (isPluggedIn: boolean) => {
      equipmentOperatorStepperUIStore.plugReeferJob(isPluggedIn)
      equipmentOperatorStepperUIStore.setHasError()
    }

    const handleRestowDestination = (newValue?: ContainerPositionType) => {
      equipmentOperatorStepperUIStore.setDestinationLocationType(newValue)
      equipmentOperatorStepperUIStore.setHasError()
    }

    const handleDirectLoadDestination = async (newValue?: DirectLoadOption) => {
      await equipmentOperatorStepperUIStore.setDirectLoadOption(newValue)
      equipmentOperatorStepperUIStore.setHasError()
    }

    return (
      <>
        {equipmentOperatorStepperUIStore.activeStep?.step === JobSteps.editDestination && (
          <EditDestination
            job={equipmentOperatorStepperUIStore.selectedJob!}
            containerOrigin={equipmentOperatorStepperUIStore.containerOrigin}
            stepsTotal={equipmentOperatorStepperUIStore.totalSteps}
            currentStep={equipmentOperatorStepperUIStore.activeStep.stepNumber}
            hasError={equipmentOperatorStepperUIStore.hasError}
            skippedEquipmentAssignment={equipmentOperatorStepperUIStore.canSkipEquipmentAssignment}
            selectedContainerNumber={equipmentOperatorStepperUIStore.containerNumber}
            yardSelector={yardSelector}
            jobWarningReason={equipmentOperatorStepperUIStore.jobWarningReason}
            handleChange={value => handleChangeDestination(value)}
            handleBlockingIssueChange={handleBlockingIssueChange}
            ConfirmPickupComponent={ConfirmPickupComponent}
            handlePicks={handlePicks}
          />
        )}

        {equipmentOperatorStepperUIStore.activeStep?.step === JobSteps.plugReefer && (
          <PlugReefer
            job={equipmentOperatorStepperUIStore.selectedJob}
            pluggedIn={equipmentOperatorStepperUIStore.isPluggedIn}
            stepsTotal={equipmentOperatorStepperUIStore.totalSteps}
            hasError={equipmentOperatorStepperUIStore.hasError}
            onPlugReeferJob={handlePluggedIn}
          />
        )}

        {equipmentOperatorStepperUIStore.activeStep?.step === JobSteps.assignEquipment && (
          <SetContainerPosition
            currentEquipmentId={
              equipmentOperatorStepperUIStore.equipmentOperatorUIStore.selectedEquipmentId
            }
            assignedEquipmentId={equipmentOperatorStepperUIStore.assignedEquipmentId}
            equipmentOptions={equipmentOperatorStepperUIStore.equipmentAssignableOptions}
            canCurrentEquipmentBeAssignedToNextJob={
              equipmentOperatorStepperUIStore.canCurrentEquipmentBeAssignedToNextJob
            }
            stepsTotal={equipmentOperatorStepperUIStore.totalSteps}
            currentStep={equipmentOperatorStepperUIStore.activeStep.stepNumber}
            hasError={equipmentOperatorStepperUIStore.hasError}
            jobWarningReason={equipmentOperatorStepperUIStore.jobWarningReason}
            entryHoldRestriction={equipmentOperatorStepperUIStore.selectedJob?.entryHoldRestriction}
            exitHoldRestriction={equipmentOperatorStepperUIStore.selectedJob?.exitHoldRestriction}
            yardHoldRestriction={equipmentOperatorStepperUIStore.selectedJob?.yardHoldRestriction}
            jobOperationType={equipmentOperatorStepperUIStore.selectedJob?.operationType}
            equipmentType={equipmentOperatorUIStore.typeOfSelectedEquipment}
            journeyId={equipmentOperatorStepperUIStore.selectedJob?.journeyId}
            yardSelector={yardSelector}
            orderId={equipmentOperatorStepperUIStore.selectedJob?.order?.externalId}
            cargoUnitNumber={equipmentOperatorStepperUIStore.containerNumber}
            containerPosition={equipmentOperatorStepperUIStore.destinationLocationType}
            hasStopped={equipmentOperatorStepperUIStore.isOutboundAndStopped}
            onPositionChange={hasSlotSelected => {
              handleChangeDestination(hasSlotSelected)
              if (
                equipmentOperatorStepperUIStore.selectedJob?.status ===
                WorkInstructionStatus.Stopped
              )
                handleEquipmentAssignment()
            }}
            onBlockingIssueChange={handleBlockingIssueChange}
            handleEquipment={(id, containerPosition) => {
              if (containerPosition) {
                equipmentOperatorStepperUIStore.setDestinationLocationType(
                  ContainerPositionType.Yard,
                )

                if (containerPosition !== ContainerPositionType.Yard) {
                  handleBlockingIssueChange(false)
                }
              } else {
                equipmentOperatorStepperUIStore.setDestinationLocationType()
                handleChangeDestination(false)
                handleBlockingIssueChange(false)
              }

              handleEquipmentAssignment(id)
            }}
          />
        )}

        {equipmentOperatorStepperUIStore.activeStep?.step === JobSteps.selectContainer && (
          <SelectContainer
            job={equipmentOperatorStepperUIStore.selectedJob!}
            cargoUnits={equipmentOperatorStepperUIStore.cargoUnits}
            selectedContainerNumber={equipmentOperatorStepperUIStore.selectedContainerNumber}
            containerOrigin={equipmentOperatorStepperUIStore.containerOrigin}
            showJourney={equipmentOperatorStepperUIStore.displayJourneyForNonNumeric}
            hasError={equipmentOperatorStepperUIStore.hasError}
            stepsTotal={equipmentOperatorStepperUIStore.totalSteps}
            currentStep={equipmentOperatorStepperUIStore.activeStep.stepNumber}
            handleSelectCargoUnit={handleChangeCargoUnit}
            handlePicks={handlePicks}
            DestinationComponent={ConfirmPickupComponent}
          />
        )}

        {equipmentOperatorStepperUIStore.activeStep?.step === JobSteps.confirmWeight && (
          <ConfirmWeight
            job={equipmentOperatorStepperUIStore.selectedJob!}
            hasError={equipmentOperatorStepperUIStore.hasError}
            stepsTotal={equipmentOperatorStepperUIStore.totalSteps}
            currentStep={equipmentOperatorStepperUIStore.activeStep.stepNumber}
            jobWarningReason={equipmentOperatorStepperUIStore.jobWarningReason}
            handleWeight={handleChangeWeight}
            handlePicks={handlePicks}
            DestinationComponent={ConfirmPickupComponent}
          />
        )}

        {equipmentOperatorStepperUIStore.activeStep?.step === JobSteps.confirmWagonNumber && (
          <ConfirmWagonNumber
            job={equipmentOperatorStepperUIStore.selectedJob!}
            hasError={equipmentOperatorStepperUIStore.hasError}
            railcarTrackPositions={equipmentOperatorStepperUIStore.railcarTrackPositions}
            containerOrigin={equipmentOperatorStepperUIStore.containerOrigin}
            currentWeight={equipmentOperatorStepperUIStore.grossWeight!}
            stepsTotal={equipmentOperatorStepperUIStore.totalSteps}
            currentStep={equipmentOperatorStepperUIStore.activeStep.stepNumber}
            handleRailcar={handleChangeRailcar}
            showDoorDirectIfExists={tenantConfigStore.tenantConfig?.skipRailTally}
            RailcarAutocompleteComponent={RailcarAutocompleteComponent}
          />
        )}

        {equipmentOperatorStepperUIStore.activeStep?.step === JobSteps.confirmPickup && (
          <CheckInformation
            job={equipmentOperatorStepperUIStore.selectedJob!}
            jobWarningReason={equipmentOperatorStepperUIStore.jobWarningReason}
            handlePicks={handlePicks}
            DestinationComponent={ConfirmPickupComponent}
          />
        )}

        {equipmentOperatorStepperUIStore.activeStep?.step === JobSteps.checkInformation && (
          <CheckInformation
            job={equipmentOperatorStepperUIStore.selectedJob!}
            jobWarningReason={equipmentOperatorStepperUIStore.jobWarningReason}
            currentStep={equipmentOperatorStepperUIStore.activeStep.stepNumber}
            stepsTotal={equipmentOperatorStepperUIStore.totalSteps}
            handlePicks={handlePicks}
            DestinationComponent={ConfirmPickupComponent}
            showDoorDirectIfExists={!tenantConfigStore.tenantConfig?.skipRailTally}
          />
        )}

        {equipmentOperatorStepperUIStore.activeStep?.step === JobSteps.restowDestination && (
          <RestowDestination
            job={equipmentOperatorStepperUIStore.selectedJob!}
            containerOrigin={equipmentOperatorStepperUIStore.containerOrigin}
            stepsTotal={equipmentOperatorStepperUIStore.totalSteps}
            jobWarningReason={equipmentOperatorStepperUIStore.jobWarningReason}
            currentStep={equipmentOperatorStepperUIStore.activeStep.stepNumber}
            hasError={equipmentOperatorStepperUIStore.hasError}
            handleChange={handleRestowDestination}
          />
        )}

        {equipmentOperatorStepperUIStore.activeStep?.step === JobSteps.directLoad && (
          <DirectLoadDestination
            job={equipmentOperatorStepperUIStore.selectedJob!}
            currentOption={equipmentOperatorStepperUIStore.directLoadOption}
            stepsTotal={equipmentOperatorStepperUIStore.totalSteps}
            jobWarningReason={equipmentOperatorStepperUIStore.jobWarningReason}
            currentStep={equipmentOperatorStepperUIStore.activeStep.stepNumber}
            hasError={equipmentOperatorStepperUIStore.hasError}
            handleChange={handleDirectLoadDestination}
          />
        )}
      </>
    )
  },
)
